import { useI18n } from '@ecom/i18n';
import { Input, Select } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import type {
  ProductSortKey,
  ProductStatusFilter,
  ProductTableState,
  SortDirection,
} from '../table-utils';

export function ProductTableToolbar({
  state,
  onChange,
}: {
  state: ProductTableState;
  onChange: (next: ProductTableState) => void;
}): ReactElement {
  const { t } = useI18n();

  const statusOptions: readonly { value: ProductStatusFilter; label: string }[] = [
    { value: 'all', label: t('merchant.products.toolbar.statusAll') },
    { value: 'draft', label: t('merchant.products.status.draft') },
    { value: 'active', label: t('merchant.products.status.active') },
    { value: 'inactive', label: t('merchant.products.status.inactive') },
  ];

  const sortOptions: readonly { value: string; label: string }[] = [
    { value: 'updatedAt:desc', label: t('merchant.products.toolbar.sortUpdatedDesc') },
    { value: 'updatedAt:asc', label: t('merchant.products.toolbar.sortUpdatedAsc') },
    { value: 'name:asc', label: t('merchant.products.toolbar.sortNameAsc') },
    { value: 'name:desc', label: t('merchant.products.toolbar.sortNameDesc') },
    { value: 'price:asc', label: t('merchant.products.toolbar.sortPriceAsc') },
    { value: 'price:desc', label: t('merchant.products.toolbar.sortPriceDesc') },
    { value: 'stock:asc', label: t('merchant.products.toolbar.sortStockAsc') },
    { value: 'stock:desc', label: t('merchant.products.toolbar.sortStockDesc') },
  ];

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <div className="min-w-[220px] flex-1">
        <Input
          type="search"
          placeholder={t('merchant.products.toolbar.searchPlaceholder')}
          value={state.search}
          onChange={(event) => {
            onChange({ ...state, search: event.target.value, page: 1 });
          }}
          aria-label={t('merchant.products.toolbar.searchAria')}
        />
      </div>

      {/* Fixed widths from sm up: the ui-kit select fills its box, so bare it would take a whole row each. */}
      <div className="w-full sm:w-48">
        <Select
          aria-label={t('merchant.products.toolbar.filterStatusAria')}
          options={statusOptions}
          value={state.status}
          required
          onChange={(event) => {
            onChange({ ...state, status: event.target.value as ProductStatusFilter, page: 1 });
          }}
        />
      </div>

      <div className="w-full sm:w-56">
        <Select
          aria-label={t('merchant.products.toolbar.sortAria')}
          options={sortOptions}
          value={`${state.sortKey}:${state.sortDirection}`}
          required
          onChange={(event) => {
            const [sortKey, sortDirection] = event.target.value.split(':') as [
              ProductSortKey,
              SortDirection,
            ];
            onChange({ ...state, sortKey, sortDirection });
          }}
        />
      </div>
    </div>
  );
}
